import { motion, AnimatePresence } from 'framer-motion';
import { Play, RotateCcw, Home, Volume2, VolumeX, Info, Trophy, Settings } from 'lucide-react';
import { Button } from '@/components/ui';

interface GameMenuProps {
  type: 'start' | 'pause' | 'gameover';
  title?: string;
  subtitle?: string;
  score?: number;
  highScore?: number;
  distance?: number;
  foodCollected?: number;
  fact?: string;
  soundEnabled?: boolean;
  onStart?: () => void;
  onResume?: () => void;
  onRestart?: () => void;
  onHome?: () => void;
  onToggleSound?: () => void;
  onSelectOtter?: () => void;
  onViewStats?: () => void;
}

export function GameMenu({
  type,
  title,
  subtitle,
  score,
  highScore,
  distance,
  foodCollected,
  fact,
  soundEnabled = true,
  onStart,
  onResume,
  onRestart,
  onHome,
  onToggleSound,
  onSelectOtter,
  onViewStats,
}: GameMenuProps) {
  const isNewHighScore = score !== undefined && highScore !== undefined && score > highScore;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
        style={{ touchAction: 'auto' }}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ type: 'spring', damping: 20 }}
          className="bg-gradient-to-b from-otter-600 to-otter-800 rounded-3xl p-6 w-full max-w-sm shadow-2xl"
          style={{ touchAction: 'auto' }}
        >
          {/* Title */}
          <h2 className="text-2xl font-bold text-white text-center mb-2">
            {title || (type === 'start' ? 'Ready to Swim?' : type === 'pause' ? 'Paused' : 'Game Over!')}
          </h2>

          {subtitle && (
            <p className="text-water-200 text-center text-sm mb-4">{subtitle}</p>
          )}

          {/* Game Over Stats */}
          {type === 'gameover' && (
            <div className="bg-black/30 rounded-xl p-4 mb-4 space-y-2">
              {/* Score with high score celebration */}
              <div className="flex justify-between items-center">
                <span className="text-water-200">Score</span>
                <div className="flex items-center gap-2">
                  <span className="text-white font-bold text-lg">
                    {score?.toLocaleString()}
                  </span>
                  {isNewHighScore && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="bg-yellow-500 text-xs px-2 py-0.5 rounded-full font-bold"
                    >
                      NEW!
                    </motion.span>
                  )}
                </div>
              </div>

              {highScore !== undefined && !isNewHighScore && (
                <div className="flex justify-between items-center">
                  <span className="text-water-200">Best</span>
                  <span className="text-yellow-400 font-bold">
                    {highScore.toLocaleString()}
                  </span>
                </div>
              )}

              {distance !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-water-200">Distance</span>
                  <span className="text-white font-bold">{Math.floor(distance)}m</span>
                </div>
              )}

              {foodCollected !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-water-200">Food Collected</span>
                  <span className="text-white font-bold">{foodCollected}</span>
                </div>
              )}
            </div>
          )}

          {/* Did You Know fact */}
          {fact && type === 'gameover' && (
            <div className="bg-water-900/50 rounded-xl p-3 mb-4">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-water-300 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-water-200 text-xs font-semibold mb-1">Did You Know?</p>
                  <p className="text-white text-sm">{fact}</p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {type === 'start' && (
              <>
                <Button
                  onClick={onStart}
                  className="w-full text-lg py-3"
                  variant="success"
                >
                  <Play className="w-5 h-5" />
                  Start Game
                </Button>

                {onSelectOtter && (
                  <Button
                    onClick={onSelectOtter}
                    className="w-full"
                    variant="secondary"
                  >
                    <Settings className="w-4 h-4" />
                    Select Otter
                  </Button>
                )}
              </>
            )}

            {type === 'pause' && (
              <>
                <Button
                  onClick={onResume}
                  className="w-full text-lg py-3"
                  variant="success"
                >
                  <Play className="w-5 h-5" />
                  Resume
                </Button>

                <Button
                  onClick={onRestart}
                  className="w-full"
                  variant="secondary"
                >
                  <RotateCcw className="w-4 h-4" />
                  Restart
                </Button>
              </>
            )}

            {type === 'gameover' && (
              <>
                <Button
                  onClick={onRestart}
                  className="w-full text-lg py-3"
                  variant="success"
                >
                  <RotateCcw className="w-5 h-5" />
                  Play Again
                </Button>

                {onSelectOtter && (
                  <Button
                    onClick={onSelectOtter}
                    className="w-full"
                    variant="secondary"
                  >
                    <Settings className="w-4 h-4" />
                    Change Otter
                  </Button>
                )}
              </>
            )}

            {/* Bottom row - Home and Sound */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={onHome}
                className="flex-1"
                variant="ghost"
              >
                <Home className="w-4 h-4" />
                Home
              </Button>

              {onToggleSound && (
                <Button
                  onClick={onToggleSound}
                  className="flex-1"
                  variant="ghost"
                >
                  {soundEnabled ? (
                    <Volume2 className="w-4 h-4" />
                  ) : (
                    <VolumeX className="w-4 h-4" />
                  )}
                  Sound
                </Button>
              )}

              {onViewStats && (
                <Button
                  onClick={onViewStats}
                  className="flex-1"
                  variant="ghost"
                >
                  <Trophy className="w-4 h-4" />
                  Stats
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
